import { Component, EventEmitter, forwardRef, Injector, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, UntypedFormControl } from '@angular/forms';
import { PickerType, SelectMode } from '@danielmoncada/angular-datetime-picker';
import { BaseControlDirective } from '../../directives/base-control.directive';
import { FormattedDatePipe } from '../../pipes/formatted-date/formatted-date.pipe';
import { OwlDatepickerCustomizer } from '../../services/owl-datepicker-customizer.service';

@Component({
  selector: 'app-datepicker-input',
  templateUrl: './datepicker-input.component.html',
  styleUrls: ['./datepicker-input.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatepickerInputComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => DatepickerInputComponent), multi: true},
  ],
})
export class DatepickerInputComponent extends BaseControlDirective implements ControlValueAccessor {
  @Input() label: string;
  @Input() panelClass = 'primary';
  @Input() pickerType: PickerType = 'calendar';
  @Input() selectMode: SelectMode = 'single';
  @Input() dateMin: Date;
  @Input() dateMax: Date;
  @Input() optional: boolean;
  @Input() autofocus: boolean;
  @Input() disable: boolean;
  @Input() iconName: boolean;
  @Input() calendarPosition: "left" | "right" = "left";
  @Input() id: string;
  

  @Input() 
  set initialValue(value: Date) {
    if(value) {
     this.dateTimeValue = value;
    }
  }

  @Output() dateTimeChange: EventEmitter<string> = new EventEmitter();
  @Output() afterPickerClosed: EventEmitter<void> = new EventEmitter();
  @Output() inputTabKeyDown: EventEmitter<void> = new EventEmitter();

  dateTimeValue: any;
  isOpen: boolean = false;

  @ViewChild('datepickerInstance') datepickerInstance;
  @ViewChild('datePickerDisplayInput') datePickerDisplayInput;
  @ViewChild('appInput') appInput;
  constructor(private formattedDatePipe: FormattedDatePipe, private owlCustomizer: OwlDatepickerCustomizer) {
    super();
  }
  
  toggleOpenClose(ev): void {
    if(!this.isOpen) {
      this.open();
    }
  }

  datepickerChange(dateObj: any): void {
    let dateString = null;
    let newDate = dateObj?.value as Date || null;
    if (this.dateTimeValue && typeof newDate?.getHours === 'function' && newDate.getHours() === 0) {
      const hr = (this.dateTimeValue as Date).getHours();
      const mn = (this.dateTimeValue as Date).getMinutes();
      newDate.setHours(hr, mn);
      if (newDate > this.dateMax) {
        newDate = this.dateMax;
      }
    }

    if (this.pickerType === 'both' && this.selectMode === 'single' && newDate) {
      dateString = this.formattedDatePipe.transform(newDate, 'MMM d a, HH:mm yyyy');
    } else if (this.selectMode === 'single' && dateObj && dateObj.value) {
      dateString = this.formattedDatePipe.transform(newDate, 'MM/dd/yyyy');
    }

    this.writeValue(dateString);
    this.dateTimeValue = newDate;
    this.dateTimeChange.emit(dateObj);
  }
  
  setCorrectMinutes(val: Date) {
    setTimeout(() => {
      this.owlCustomizer.setTime(new Date(this.dateTimeValue).getHours(), new Date(this.dateTimeValue).getMinutes());
    });
  }
  
  writeValue(v: string): void {
    super.writeValue(v);
    this.value = v;
  }

  datepickerOpened(): void {
    this.isOpen = true;
    if(this.pickerType === 'both') {
      setTimeout(() => {
        this.owlCustomizer.customizeOwlDateTimer();
        this.focus();
        this.owlCustomizer.stopSetButtonEnterKeyDownPropagation();
      });
    }
  }

  datepickerClosed(): void {
    this.isOpen = false;
    this.afterPickerClosed.emit();
  }

  backspace(): void {
    this.dateTimeValue = null;
    this.writeValue('');
    this.control.patchValue('');
  }

  inputBlur(): void {
    this.writeValue(this.control.value);
  }

  validate(c: UntypedFormControl): any {
    if (!c.pristine) {
      return super.validate(c);
    }
  }

  focus(): void {
    this.owlCustomizer.focus();
  }

  open(): void {
    this.datepickerInstance?.open();
  }

  //focus app-input
  focusInput(): void {
    this.appInput.focusInput();
  }

  reset(): void {
    this.dateTimeValue = null;
    this.writeValue('');
    this.datepickerInstance._dtInput._values = [];
  }

  setValue(value: Date[]): void {
    this.datepickerInstance._dtInput._values = value;
  }
}
